/**
 * Admin Portal — Knowledge Base Management page.
 * Upload documents (.xlsx, .pdf, .docx) to Azure AI Search indexing pipeline.
 */
import React, { useEffect, useState, useRef } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, IconButton, Tooltip,
} from '@mui/material';
import { Upload, Delete } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

interface KBUpload {
  id: string;
  domain: string;
  file_name: string | null;
  question_count: number | null;
  version: number;
  status: string;
  index_name: string | null;
  created_at: string | null;
}

const DOMAINS = [
  'Cloud Security Adoption', 'Zero Trust Maturity', 'Data Security Copilot',
  'Azure AI Readiness', 'AI Engineer Skills', 'GenAI Maturity',
  'DevOps Capability', 'App Data Modernization', 'DevSecOps Capability',
  'Cloud Maturity', 'Azure Landing Zone', 'WAF Assessment',
  'M365 Copilot Readiness', 'Power Platform Adoption',
];

const KnowledgeBase: React.FC = () => {
  const [uploads, setUploads] = useState<KBUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [domain, setDomain] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchUploads = () => {
    setLoading(true);
    authFetch('/api/admin/portal/knowledge-base/uploads')
      .then((r) => r.json())
      .then((data) => { if (data.success) setUploads(data.uploads); })
      .catch(() => setError('Failed to load uploads'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });
  };

  useEffect(() => { fetchUploads(); }, []);

  const handleUpload = async () => {
    if (!selectedFile || !domain) return;

    // Client-side empty file check
    if (selectedFile.size === 0) {
      setError('Selected file is empty. Please choose a valid document.');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('domain', domain);

      const response = await authFetch('/api/admin/portal/knowledge-base/upload', {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();

      if (response.ok && result.success) {
        const msg = result.question_count
          ? `Indexed ${result.question_count} questions and ${result.knowledge_count || 0} knowledge docs (v${result.version})`
          : `File uploaded for indexing (v${result.version}). ${result.note || ''}`;
        setSuccess(msg);
        setUploadDialog(false);
        setDomain('');
        setSelectedFile(null);
        fetchUploads();
      } else {
        setError(result.error || result.detail || 'Upload failed');
      }
    } catch {
      setError('Failed to upload — check network connection');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (uploadId: string) => {
    if (!window.confirm('Are you sure you want to delete this upload?')) return;
    try {
      const res = await authFetch(`/api/admin/portal/knowledge-base/${uploadId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setSuccess('Upload deleted');
        fetchUploads();
      } else {
        setError('Failed to delete upload');
      }
    } catch {
      setError('Delete failed — check network');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Knowledge Base
        </Typography>
        <RefreshButton onRefresh={fetchUploads} loading={loading} lastRefreshed={lastRefreshed} />
        <Button variant="contained" startIcon={<Upload />} onClick={() => setUploadDialog(true)}>
          Upload Document
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload assessment files (.xlsx, .pdf, .docx) to the Azure AI Search knowledge base.
        Excel files are parsed into structured questions. PDF and Word documents are indexed via Azure document cracking.
      </Typography>

      {uploads.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No knowledge base uploads yet.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>File</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Questions</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploads.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.domain}</TableCell>
                  <TableCell>{u.file_name || '—'}</TableCell>
                  <TableCell>{u.question_count || '—'}</TableCell>
                  <TableCell>
                    <Chip label={`v${u.version}`} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={u.status} size="small" color={
                      u.status === 'indexed' ? 'success' :
                      u.status === 'failed' ? 'error' : 'warning'
                    } />
                  </TableCell>
                  <TableCell>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete upload">
                      <IconButton size="small" color="error" onClick={() => handleDelete(u.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => !uploading && setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Knowledge Base Document</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          >
            {DOMAINS.map((d) => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </TextField>

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.pdf,.docx"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Button
            variant="outlined"
            fullWidth
            onClick={() => fileInputRef.current?.click()}
            sx={{ mb: 1, py: 1.5 }}
          >
            {selectedFile ? selectedFile.name : 'Select file (.xlsx, .pdf, .docx)'}
          </Button>
          {selectedFile && (
            <Typography variant="caption" color="text.secondary">
              {(selectedFile.size / 1024).toFixed(1)} KB
            </Typography>
          )}

          {uploading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)} disabled={uploading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!domain || !selectedFile || uploading}
          >
            {uploading ? 'Processing…' : 'Upload & Index'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KnowledgeBase;
